
export const caseStudies = [
  {
    id: "1",
    title: "Modern E-commerce Platform",
    category: "E-commerce",
    description: "A luxury shopping experience with AI-powered product recommendations and seamless checkout.",
    client: "LuxRetail Inc.",
    duration: "3 months",
    date: "March 2023",
    challenge: "LuxRetail needed a modern e-commerce platform that could handle their extensive product catalog while providing a premium shopping experience. Their existing solution was slow, difficult to maintain, and lacked personalization features that modern shoppers expect.",
    solution: "We built a custom e-commerce platform using React for the frontend and a headless CMS for content management. The platform incorporates AI-powered product recommendations, personalized shopping experiences, and a streamlined checkout process. We implemented advanced search capabilities with filters and sorting options, and integrated with multiple payment processors for a seamless payment experience.",
    results: [
      "54% increase in conversion rate within the first month",
      "Average order value increased by 27%",
      "Page load time reduced by 65%",
      "Mobile transactions increased by 48%"
    ],
    technologies: ["React", "Next.js", "Tailwind CSS", "Stripe", "Sanity CMS", "TensorFlow.js", "Algolia"],
    imageUrl: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d",
    gallery: [
      "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d",
      "https://images.unsplash.com/photo-1557821552-17105176677c",
      "https://images.unsplash.com/photo-1600508774634-4e11d34730e2"
    ],
    testimonial: {
      quote: "The AI-powered recommendations have been a game-changer for our business. Customers are discovering products they love, and our average order value has increased significantly. The team delivered a truly premium solution that exceeded our expectations.",
      author: "Alexandra Chen",
      position: "CEO, LuxRetail Inc."
    },
    link: "/services/portfolio/1"
  },
  {
    id: "2",
    title: "Financial Dashboard",
    category: "Fintech",
    description: "Real-time analytics dashboard with predictive insights for financial institutions.",
    client: "Capital Analytics Partners",
    duration: "4 months",
    date: "November 2022",
    challenge: "Capital Analytics Partners was struggling with outdated data visualization tools that couldn't handle the volume and complexity of financial data they needed to analyze. Their analysts were spending too much time preparing reports instead of deriving actionable insights.",
    solution: "We developed a comprehensive financial dashboard that consolidates data from multiple sources and presents it through interactive visualizations. The dashboard includes real-time updates, predictive analytics powered by machine learning, and customizable reports. The system automatically detects anomalies and trends, alerting users to potential opportunities or risks.",
    results: [
      "Reduced report generation time by 85%",
      "Identified $3.7M in cost-saving opportunities within first quarter",
      "Improved decision-making speed by 40%",
      "Increased analyst productivity by 62%"
    ],
    technologies: ["React", "TypeScript", "D3.js", "Recharts", "TensorFlow", "AWS Lambda", "PostgreSQL"],
    imageUrl: "https://images.unsplash.com/photo-1531297484001-80022131f5a1",
    gallery: [
      "https://images.unsplash.com/photo-1531297484001-80022131f5a1",
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f",
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
    ],
    testimonial: {
      quote: "This dashboard has transformed how we analyze financial data. What used to take days now happens in real-time, and the predictive insights have helped us stay ahead of market trends. The ROI on this project has been extraordinary.",
      author: "Michael Rivera",
      position: "CTO, Capital Analytics Partners"
    },
    link: "/services/portfolio/2"
  },
  {
    id: "3",
    title: "Healthcare Patient Portal",
    category: "Healthcare",
    description: "Secure, HIPAA-compliant patient portal with AI-assisted appointment scheduling.",
    client: "MediCare Solutions",
    duration: "6 months",
    date: "July 2022",
    challenge: "MediCare Solutions needed a patient portal that could streamline the healthcare experience while ensuring the highest levels of security and compliance. Their patients were frustrated with complicated scheduling processes and limited access to their medical information.",
    solution: "We built a comprehensive patient portal that puts patients in control of their healthcare journey. The portal includes features like AI-assisted appointment scheduling, secure messaging with healthcare providers, access to medical records and test results, prescription refill requests, and telemedicine integration. The entire system is HIPAA-compliant with end-to-end encryption.",
    results: [
      "Reduced appointment no-shows by 58%",
      "Decreased administrative workload by 40%",
      "Improved patient satisfaction scores by 47%",
      "Increased telemedicine adoption by 75%"
    ],
    technologies: ["React", "Node.js", "Express", "MongoDB", "TensorFlow.js", "AWS HIPAA-eligible services", "Socket.io"],
    imageUrl: "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7",
    gallery: [
      "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7",
      "https://images.unsplash.com/photo-1505751172876-fa1923c5c528",
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef"
    ],
    testimonial: {
      quote: "The patient portal has revolutionized how we interact with our patients. The AI-assisted scheduling has drastically reduced no-shows, and patients love having access to their medical information. Most importantly, we have complete confidence in the security of the system.",
      author: "Dr. Sarah Johnson",
      position: "Medical Director, MediCare Solutions"
    },
    link: "/services/portfolio/3"
  },
  {
    id: "4",
    title: "Real Estate Listing Platform",
    category: "Real Estate",
    description: "Interactive property listings with virtual tours and neighborhood insights.",
    client: "Prime Properties",
    duration: "4 months",
    date: "January 2023",
    challenge: "Prime Properties wanted to differentiate itself in the competitive real estate market by offering more immersive property listings and deeper neighborhood insights. Traditional listing platforms were providing a limited view of properties and lacked contextual information about neighborhoods.",
    solution: "We developed a cutting-edge real estate platform featuring 3D virtual tours, AI-powered property recommendations, and comprehensive neighborhood profiles. The platform integrates with public data sources to provide information on schools, crime rates, amenities, and property value trends. Users can filter properties based on highly specific criteria and receive alerts when matching properties become available.",
    results: [
      "Property viewing requests increased by 67%",
      "Average time to sell reduced by 23 days",
      "User engagement increased by 89%",
      "Customer acquisition cost reduced by 42%"
    ],
    technologies: ["React", "Three.js", "Next.js", "PostgreSQL", "MapBox API", "TensorFlow", "AWS S3"],
    imageUrl: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
    gallery: [
      "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
      "https://images.unsplash.com/photo-1582407947304-fd86f028f716",
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa"
    ],
    testimonial: {
      quote: "This platform has completely transformed our business. The virtual tours have been a game-changer, especially for international clients, and the neighborhood insights give our listings valuable context. We're closing deals faster and attracting more premium clients.",
      author: "Jessica Torres",
      position: "CEO, Prime Properties"
    },
    link: "/services/portfolio/4"
  },
  {
    id: "5",
    title: "Educational Learning Management",
    category: "Education",
    description: "Adaptive learning platform with personalized curriculum and progress tracking.",
    client: "EduTech Innovations",
    duration: "5 months",
    date: "September 2022",
    challenge: "EduTech Innovations wanted to create a learning management system that could adapt to individual student needs and learning styles. Traditional LMS platforms offered a one-size-fits-all approach that wasn't effective for many students.",
    solution: "We built an adaptive learning platform that uses AI to personalize the educational experience for each student. The system analyzes learning patterns, identifies strengths and weaknesses, and adjusts content difficulty accordingly. Features include interactive lessons, spaced repetition for better retention, gamification elements, detailed progress tracking, and integration with various educational content providers.",
    results: [
      "Average student test scores improved by 32%",
      "Course completion rates increased by 47%",
      "Student engagement time doubled",
      "Teacher time spent on administrative tasks reduced by 60%"
    ],
    technologies: ["React", "Python", "Django", "TensorFlow", "PostgreSQL", "WebSockets", "AWS"],
    imageUrl: "https://images.unsplash.com/photo-1518770660439-4636190af475",
    gallery: [
      "https://images.unsplash.com/photo-1518770660439-4636190af475",
      "https://images.unsplash.com/photo-1509062522246-3755977927d7",
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b"
    ],
    testimonial: {
      quote: "The adaptive learning capabilities of this platform have exceeded our expectations. We're seeing remarkable improvements in student outcomes across all ability levels. The personalized approach keeps students engaged, and the analytics provide invaluable insights for our educators.",
      author: "Robert Chen",
      position: "Founder, EduTech Innovations"
    },
    link: "/services/portfolio/5"
  },
  {
    id: "6",
    title: "Travel Booking Application",
    category: "Travel",
    description: "All-in-one travel booking with AI-recommended itineraries and real-time updates.",
    client: "Wanderlust Travels",
    duration: "4 months",
    date: "April 2023",
    challenge: "Wanderlust Travels wanted to create a comprehensive travel booking platform that would simplify the planning process and provide personalized recommendations. Travelers were overwhelmed by the fragmented nature of travel planning across multiple websites and apps.",
    solution: "We developed an all-in-one travel application that handles everything from flights and accommodations to activities and dining. The app features AI-generated itineraries based on user preferences, budget constraints, and travel history. It provides real-time updates on flight status, weather conditions, and local events. The platform also incorporates user reviews, virtual tours of hotels and attractions, and offline access to important travel documents.",
    results: [
      "Conversion rate increased by 43%",
      "Average booking value increased by 37%",
      "Customer satisfaction score of 4.8/5",
      "User retention rate improved by 62%"
    ],
    technologies: ["React Native", "Node.js", "GraphQL", "MongoDB", "AWS Lambda", "TensorFlow", "Google Maps API"],
    imageUrl: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6",
    gallery: [
      "https://images.unsplash.com/photo-1461749280684-dccba630e2f6",
      "https://images.unsplash.com/photo-1503220317375-aaad61436b1b",
      "https://images.unsplash.com/photo-1473625247510-8ceb1760943f"
    ],
    testimonial: {
      quote: "This app has revolutionized how our customers plan their travels. The AI recommendations are remarkably accurate, and having everything in one place has been a huge selling point. We've seen substantial growth in both new users and repeat bookings since launching.",
      author: "Thomas Patel",
      position: "Director of Digital, Wanderlust Travels"
    },
    link: "/services/portfolio/6"
  }
];
